import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { getApiBase } from '../../lib/utils'

type ApiKeyStatus = {
  has_api_key: boolean
  user_id: number
  email: string
}

export default function ApiKeyPage() {
  const [apiKeyStatus, setApiKeyStatus] = useState<ApiKeyStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  const apiBase = useMemo(() => {
    if (typeof window === 'undefined') return getApiBase()
    return `${window.location.origin}${getApiBase()}`
  }, [])

  const loadApiKeyStatus = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/fapi/users/me/api-key/status')
      setApiKeyStatus(data)
    } catch (error) {
      console.error('Failed to load API key status', error)
      setApiKeyStatus(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadApiKeyStatus()
  }, [loadApiKeyStatus])

  const handleGenerate = useCallback(async () => {
    if (!confirm('Générer une nouvelle clé API ? Si vous en avez déjà une, elle sera remplacée et toutes vos intégrations actuelles cesseront de fonctionner.')) {
      return
    }
    
    try {
      setGenerating(true)
      const { data } = await api.post('/fapi/users/me/api-key/generate')
      setGeneratedKey(data.api_key)
      await loadApiKeyStatus()
    } catch (error) {
      console.error('Failed to generate API key', error)
      alert('Erreur lors de la génération de la clé API')
    } finally {
      setGenerating(false)
    }
  }, [loadApiKeyStatus])

  const handleRevoke = useCallback(async () => {
    if (!confirm('Révoquer votre clé API ? Toutes vos intégrations cesseront de fonctionner immédiatement.')) {
      return
    }
    
    try {
      setRevoking(true)
      await api.delete('/fapi/users/me/api-key')
      setGeneratedKey(null)
      await loadApiKeyStatus()
      alert('Clé API révoquée avec succès')
    } catch (error) {
      console.error('Failed to revoke API key', error)
      alert('Erreur lors de la révocation de la clé API')
    } finally {
      setRevoking(false)
    }
  }, [loadApiKeyStatus])

  const handleCopyKey = useCallback(async () => {
    if (!generatedKey) return
    try {
      await navigator.clipboard.writeText(generatedKey)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2500)
    } catch (error) {
      console.error('Clipboard error', error)
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 2500)
    }
  }, [generatedKey])

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(apiBase)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2500)
    } catch (error) {
      console.error('Clipboard error', error)
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 2500)
    }
  }, [apiBase])

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-sky-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        <div className="mb-6">
          <Link
            to="/integrations"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour aux intégrations
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-white">Clé API Totemium</h1>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">Générez votre clé API pour les intégrations externes</p>
        </div>

        <article className="relative overflow-hidden rounded-3xl border border-zinc-200/80 bg-white/95 p-6 shadow-md backdrop-blur dark:border-zinc-800/70 dark:bg-zinc-900/85">
          <div aria-hidden className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-sky-400/15 blur-3xl dark:bg-sky-500/20" />
          <div className="relative space-y-4">
            {loading ? (
              <div className="text-sm text-zinc-500">Chargement...</div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">URL API Totemium</label>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
                      {apiBase}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopyUrl}
                      className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                    >
                      Copier
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Votre Clé API</label>
                  
                  {generatedKey ? (
                    <div className="mt-2 space-y-3">
                      <div className="rounded-2xl border-2 border-emerald-500/30 bg-emerald-50/50 p-4 dark:border-emerald-500/20 dark:bg-emerald-900/10">
                        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Clé générée avec succès !
                        </div>
                        <p className="mb-3 text-xs text-emerald-600 dark:text-emerald-400">
                          ⚠️ Copiez cette clé maintenant. Elle ne sera plus affichée après fermeture de cette page.
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 overflow-x-auto rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-emerald-700 dark:bg-zinc-800 dark:text-zinc-100">
                            {generatedKey}
                          </code>
                          <button
                            type="button"
                            onClick={handleCopyKey}
                            className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600"
                          >
                            Copier
                          </button>
                        </div>
                        {copyState === 'copied' && (
                          <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-300">✓ Clé copiée !</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setGeneratedKey(null)}
                        className="text-sm text-zinc-500 underline hover:text-zinc-700 dark:hover:text-zinc-300"
                      >
                        Masquer la clé
                      </button>
                    </div>
                  ) : apiKeyStatus?.has_api_key ? (
                    <div className="mt-2 space-y-3">
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800">
                        <div className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                          <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Vous avez déjà une clé API active
                        </div>
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                          Pour des raisons de sécurité, la clé n'est pas affichée. Si vous l'avez perdue, générez-en une nouvelle.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleGenerate}
                          disabled={generating}
                          className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {generating ? 'Génération...' : 'Régénérer une nouvelle clé'}
                        </button>
                        <button
                          type="button"
                          onClick={handleRevoke}
                          disabled={revoking}
                          className="rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800 dark:bg-zinc-900 dark:text-rose-400 dark:hover:bg-rose-900/20"
                        >
                          {revoking ? 'Révocation...' : 'Révoquer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 space-y-3">
                      <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          Vous n'avez pas encore de clé API. Générez-en une pour utiliser les intégrations externes.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerate}
                        disabled={generating}
                        className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {generating ? 'Génération...' : 'Générer ma clé API'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-sky-200/70 bg-sky-50/60 px-4 py-3 text-sm dark:border-sky-800 dark:bg-sky-900/20">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-100">
                    Comment utiliser votre clé API ?
                  </h3>
                  <ul className="space-y-2 text-xs text-sky-800 dark:text-sky-200 sm:text-sm">
                    <li>• <strong>WordPress :</strong> Copiez l'URL API et la clé dans les réglages du plugin Totemium</li>
                    <li>• <strong>Script embarqué :</strong> Ajoutez <code className="rounded bg-sky-100 px-1 dark:bg-sky-900">data-token="votre-clé"</code> dans le script</li>
                    <li>• <strong>API REST :</strong> Envoyez la clé dans le header <code className="rounded bg-sky-100 px-1 dark:bg-sky-900">Authorization: Bearer votre-clé</code></li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  )
}
